// Delivery Notes data layer (Phase 4) — header + line items.
//
// The SINGLE interface between the app and the delivery-note schema
// (supabase/migrations/20260630211002_delivery_notes.sql).
//
// SECURITY MODEL (server-enforced; mirrored here):
//   • We NEVER send company_id or dn_number — DB triggers own them (company
//     forced/inherited; dn_number auto = DN-00001; both immutable). RLS scopes all.
//
// INVENTORY INTEGRATION (the defining requirement):
//   • Dispatch is the ONLY thing that reduces current_stock. After every write we call
//     inventory.syncDeliveryNote (idempotent): notes in a dispatched/delivered/returned
//     status post 'sale' (current↓) and 'return' (current↑) movements, then the linked
//     Sales Order is re-synced so its reservation shrinks to the outstanding quantity
//     and its fulfilment status advances. Delete restores. Dispatch is capped at the
//     reserved quantity — you can never dispatch above what's reserved.
//   • Supports partial deliveries and multiple notes per Sales Order.
//
// RETURN-TYPE CONTRACT: collection → { data, count, page?, pageSize? } · single → T|null
//   · create/update → id · delete → void · all THROW a mapped Error.

import { supabase } from "./supabaseClient";
import { syncDeliveryNote, removeDeliveryNote } from "./inventory";
import { C } from "../customers/theme";

const DN = "delivery_notes";
const LINES = "delivery_note_lines";

export const DEFAULT_PAGE_SIZE = 25;

/** Colour-coded statuses (+ DB CHECK parity). */
export const DN_STATUSES = [
  { value: "draft", label: "Draft", color: C.textMuted },
  { value: "picking", label: "Picking", color: C.primary },
  { value: "packed", label: "Packed", color: "#0ea5e9" },
  { value: "dispatched", label: "Dispatched", color: C.warning },
  { value: "delivered", label: "Delivered", color: C.success },
  { value: "returned", label: "Returned", color: C.purple },
  { value: "cancelled", label: "Cancelled", color: C.danger },
];

/** Statuses that have physically moved stock (mirror inventory.DISPATCH_STATUSES). */
export const DISPATCH_STATUSES = ["dispatched", "delivered", "returned"];

export const DN_SORTS = [
  { value: "number", label: "DN No." },
  { value: "date", label: "Delivery Date" },
  { value: "status", label: "Status" },
  { value: "created", label: "Newest" },
];

const HEADER_WRITE_FIELDS = ["customer_id", "customer_name", "sales_order_id", "sales_order_number", "delivery_date", "driver", "vehicle", "reference", "status", "notes"];
const LINE_WRITE_FIELDS = ["sales_order_line_id", "product_id", "sku", "description", "ordered_quantity", "reserved_quantity", "previously_dispatched", "dispatched_quantity", "delivered_quantity", "returned_quantity", "unit_price"];
const SORT_COLUMNS = { number: "dn_number", date: "delivery_date", status: "status", created: "created_at" };

const HEADER_SELECT =
  "id, dn_number, status, delivery_date, driver, vehicle, reference, customer_id, customer_name, " +
  "sales_order_id, sales_order_number, created_at, updated_at, " +
  "customers ( id, company_name ), sales_orders ( id, so_number )";
const FULL_SELECT =
  "*, customers ( id, company_name ), sales_orders ( id, so_number, status ), " +
  "delivery_note_lines ( * )";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");
const num = (n) => Number(n) || 0;
const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = "Invalid value — check the delivery status."; break;
    case "23503": message = "Referenced sales order, customer or product does not exist."; break;
    case "23502": message = "A required field is missing."; break;
    case "PGRST116": message = "Delivery note not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List delivery notes (RLS-scoped) with search, status/customer filters, sort, pagination.
 * @param {Object} [opts]
 * @returns {Promise<{data:Array,count:number,page:number,pageSize:number}>}
 */
export async function getDeliveryNotes(opts = {}) {
  const { search, status, customerId, page = 1, pageSize = DEFAULT_PAGE_SIZE, sortBy = "created", sortDir = "desc" } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "created_at";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safeSize;
  let q = c.from(DN).select(HEADER_SELECT, { count: "exact" }).order(column, { ascending: sortDir !== "desc" }).range(from, from + safeSize - 1);
  if (status && status !== "all") q = q.eq("status", status);
  if (customerId && customerId !== "all") q = q.eq("customer_id", customerId);
  const term = sanitizeTerm(search);
  if (term) q = q.or(`dn_number.ilike.%${term}%,customer_name.ilike.%${term}%,sales_order_number.ilike.%${term}%,reference.ilike.%${term}%,driver.ilike.%${term}%`);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load delivery notes");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

export async function searchDeliveryNotes(term, opts = {}) { return getDeliveryNotes({ ...opts, search: term }); }

/** Full delivery note (header + customer + sales order + lines, ordered). */
export async function getDeliveryNote(id) {
  const { data, error } = await client().from(DN).select(FULL_SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load delivery note");
  if (data?.delivery_note_lines) data.delivery_note_lines.sort((a, b) => (a.position || 0) - (b.position || 0));
  return data || null;
}

/**
 * Sales orders with stock available to dispatch (status confirmed / partially_fulfilled),
 * with customer + lines (reserved + already-dispatched) so the form can build deliverable
 * lines. allocated_quantity carries the quantity already dispatched on earlier notes.
 * @returns {Promise<{data:Array}>}
 */
export async function getDispatchableSalesOrders() {
  const { data, error } = await client().from("sales_orders")
    .select("id, so_number, status, customer_id, customer_name, sales_order_lines ( id, product_id, sku, description, quantity, reserved_quantity, allocated_quantity, unit_price )")
    .in("status", ["confirmed", "partially_fulfilled"])
    .order("so_number", { ascending: true });
  if (error) throw toError(error, "Could not load sales orders");
  return { data: data || [] };
}

// ── Writes ───────────────────────────────────────────────────────────────────

async function replaceLines(dnId, lines) {
  const c = client();
  const { error: delErr } = await c.from(LINES).delete().eq("delivery_note_id", dnId);
  if (delErr) throw toError(delErr, "Could not update delivery lines");
  const rows = (lines || [])
    .filter((l) => l.product_id || (l.description && l.description.trim()) || num(l.dispatched_quantity) > 0 || num(l.returned_quantity) > 0)
    .map((l, i) => {
      // CAP: never dispatch above the reserved (held) quantity, nor above the outstanding order
      const maxByOrder = Math.max(0, round3(num(l.ordered_quantity) - num(l.previously_dispatched)));
      const cap = Math.min(num(l.reserved_quantity) || maxByOrder, maxByOrder || Infinity);
      const dispatched = Math.max(0, Math.min(num(l.dispatched_quantity), Number.isFinite(cap) ? cap : num(l.dispatched_quantity)));
      const outstanding = Math.max(0, round3(num(l.ordered_quantity) - num(l.previously_dispatched) - dispatched));
      return {
        ...pick({ ...l, dispatched_quantity: dispatched }, LINE_WRITE_FIELDS),
        outstanding_quantity: outstanding, position: i, delivery_note_id: dnId,
      };
    });
  if (rows.length) {
    const { error } = await c.from(LINES).insert(rows);
    if (error) throw toError(error, "Could not save delivery lines");
  }
}

/**
 * Create a delivery note with its lines, then post inventory movements + re-sync the SO.
 * @param {Object} payload Header fields + `lines: Array`.
 * @returns {Promise<string>} new DN id.
 */
export async function createDeliveryNote(payload) {
  const c = client();
  const { data, error } = await c.from(DN).insert(pick(payload || {}, HEADER_WRITE_FIELDS)).select("id").single();
  if (error) throw toError(error, "Could not create delivery note");
  await replaceLines(data.id, payload?.lines || []);
  await syncDeliveryNote(data.id); // dispatch → current↓, reservation released, SO fulfilment updated
  return data.id;
}

/**
 * Update a delivery note header + replace its lines, then re-reconcile inventory.
 * @param {string} id @param {Object} payload Header fields + `lines: Array`.
 * @returns {Promise<string>} the DN id.
 */
export async function updateDeliveryNote(id, payload) {
  const c = client();
  const { error } = await c.from(DN).update(pick(payload || {}, HEADER_WRITE_FIELDS)).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update delivery note");
  await replaceLines(id, payload?.lines || []);
  await syncDeliveryNote(id);
  return id;
}

/**
 * Delete a delivery note (cascades its lines), then roll its stock back + re-sync the SO.
 * @param {string} id @returns {Promise<void>}
 */
export async function deleteDeliveryNote(id) {
  const c = client();
  const { data: dn } = await c.from(DN).select("sales_order_id").eq("id", id).maybeSingle();
  const { error } = await c.from(DN).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete delivery note");
  await removeDeliveryNote(id, dn?.sales_order_id);
}
